import { useAuth } from 'wasp/client/auth';
import {
  generateCheckoutSession,
  getCustomerPortalUrl,
  useQuery,
} from 'wasp/client/operations';
import {
  PaymentPlanId,
  isActiveRecurringSubscriptionStatus,
  paymentPlans,
  prettyPaymentPlanName,
} from './plans';
import { AiFillCheckCircle } from 'react-icons/ai';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/common';
import { cn } from '../client/cn';

const bestDealPaymentPlanId: PaymentPlanId = PaymentPlanId.Supporter;

interface PaymentPlanCard {
  name: string;
  price: string;
  description: string;
  features: string[];
}

export const paymentPlanCards: Record<PaymentPlanId, PaymentPlanCard> = {
  [PaymentPlanId.Free]: {
    name: prettyPaymentPlanName(PaymentPlanId.Free),
    price: '$0',
    description: 'All features included',
    features: [
      'Unlimited articles',
      'Full access to all content',
      'Community support',
      'No credit card required',
    ],
  },
  [PaymentPlanId.Supporter]: {
    name: prettyPaymentPlanName(PaymentPlanId.Supporter),
    price: '$1',
    description: 'One-time support for our mission',
    features: [
      'All Free features',
      'One-time contribution',
      'Priority support',
      'Supporter badge',
    ],
  },
  [PaymentPlanId.SuperSupporter]: {
    name: prettyPaymentPlanName(PaymentPlanId.SuperSupporter),
    price: '$5',
    description: 'Larger one-time support for our mission',
    features: [
      'All Supporter features',
      'Bigger one-time contribution',
      'Priority support',
      'Super supporter badge',
    ],
  },
};

const PricingPage = () => {
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);

  const { data: user } = useAuth();
  const isRecurringSubscriber = isActiveRecurringSubscriptionStatus(
    user?.subscriptionStatus,
  );
  const isOneTimeSupporter = user?.subscriptionStatus === 'paid_once';

  const {
    data: customerPortalUrl,
    isLoading: isCustomerPortalUrlLoading,
    error: customerPortalUrlError,
  } = useQuery(getCustomerPortalUrl, undefined, {
    enabled: isRecurringSubscriber,
  });

  const navigate = useNavigate();

  async function handleBuyNowClick(paymentPlanId: PaymentPlanId) {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      setIsPaymentLoading(true);

      const checkoutResults = await generateCheckoutSession(paymentPlanId);

      if (checkoutResults?.sessionUrl) {
        window.open(checkoutResults.sessionUrl, '_self');
      } else {
        throw new Error('Error generating checkout session URL');
      }
    } catch (error) {
      console.error(error);
      setIsPaymentLoading(false); // We only set this to false here and not in the try block because we redirect to the checkout url within the same window
    }
  }

  const handleCustomerPortalClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (customerPortalUrlError) {
      console.error('Error fetching customer portal url');
    }

    if (!customerPortalUrl) {
      throw new Error(`Customer Portal does not exist for user ${user.id}`);
    }

    window.open(customerPortalUrl, '_blank');
  };

  return (
    <div className="py-10 lg:mt-10">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div id="pricing" className="mx-auto max-w-4xl text-center">
          <h2 className="mt-2 text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl dark:text-white">
            Support <span className="text-pink-500">Optimistic News</span>
          </h2>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-neutral-600 dark:text-white">
          All features remain free. Supporter tiers are one-time contributions
          that help cover our costs. <br />
        </p>
        <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-y-8 lg:gap-x-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {Object.values(PaymentPlanId).map((planId) => {
            const isFreePlan = planId === PaymentPlanId.Free;
            const isBestDealPlan = planId === bestDealPaymentPlanId;
            const isManageSupportAction = !isFreePlan && isRecurringSubscriber;
            const ctaLabel = isFreePlan
              ? 'Always free'
              : isManageSupportAction
                ? 'Manage Recurring Support'
                : !user
                  ? 'Log in to support'
                  : isOneTimeSupporter
                    ? 'Support again'
                    : 'Support once';
            const ctaDisabled =
              isFreePlan ||
              (isManageSupportAction
                ? isCustomerPortalUrlLoading
                : isPaymentLoading);
            const ctaDescriptionId = isManageSupportAction
              ? 'manage-recurring-support'
              : planId;
            const handlePlanCtaClick = () => {
              if (isFreePlan) {
                return;
              }

              if (isManageSupportAction) {
                handleCustomerPortalClick();
                return;
              }

              void handleBuyNowClick(planId);
            };

            return (
              <div
                key={planId}
                className={cn(
                  'relative flex flex-col grow justify-between rounded-3xl overflow-hidden p-8 xl:p-10',
                  {
                    'ring-2 ring-primary/60 dark:ring-primary/40 lg:scale-105':
                      isBestDealPlan,
                    'ring-1 ring-neutral-900/10 dark:ring-neutral-100/10 lg:mt-8':
                      !isBestDealPlan,
                  },
                )}
              >
                {isBestDealPlan && (
                  <div
                    className="absolute inset-0 -z-10 rounded-3xl bg-primary-50 dark:bg-primary-950/30"
                    aria-hidden="true"
                  />
                )}
                <div className="mb-8">
                  <div className="flex items-center justify-between gap-x-4">
                    <h3
                      id={planId}
                      className="text-neutral-900 text-lg font-semibold leading-8 dark:text-white"
                    >
                      {paymentPlanCards[planId].name}
                    </h3>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-neutral-600 dark:text-white">
                    {paymentPlanCards[planId].description}
                  </p>
                  <p className="mt-6 flex items-baseline gap-x-1 dark:text-white">
                    <span className="text-4xl font-bold tracking-tight text-neutral-900 dark:text-white">
                      {paymentPlanCards[planId].price}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-neutral-600 dark:text-white">
                      {planId !== PaymentPlanId.Free &&
                        paymentPlans[planId].effect.kind === 'subscription' &&
                        '/month'}
                      {paymentPlans[planId].effect.kind ===
                        'one_time_supporter' && '/once'}
                    </span>
                  </p>
                  <ul
                    role="list"
                    className="mt-8 space-y-3 text-sm leading-6 text-neutral-600 dark:text-white"
                  >
                    {paymentPlanCards[planId].features.map((feature) => (
                      <li key={feature} className="flex gap-x-3">
                        <AiFillCheckCircle
                          className="h-6 w-5 flex-none text-pink-500"
                          aria-hidden="true"
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  type="button"
                  variant={isBestDealPlan ? 'solid' : 'outline'}
                  tone={isBestDealPlan ? 'primary' : 'neutral'}
                  size="sm"
                  onClick={handlePlanCtaClick}
                  disabled={ctaDisabled}
                  aria-describedby={ctaDescriptionId}
                  className={cn(
                    'mt-8 block w-full rounded-md px-3 py-2 text-center font-semibold leading-6 focus-visible:ring-pink-400',
                    {
                      'bg-pink-500 text-white shadow-xs hover:bg-pink-400 hover:text-white':
                        isBestDealPlan,
                      'border-transparent text-neutral-600 ring-1 ring-inset ring-secondary-200 hover:bg-transparent hover:ring-secondary-400 dark:text-white':
                        !isBestDealPlan,
                      'disabled:!cursor-wait cursor-wait opacity-50':
                        !isFreePlan &&
                        !isManageSupportAction &&
                        isPaymentLoading,
                    },
                  )}
                >
                  {ctaLabel}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
